/**
 * Staff actions for Phased Project Structures: releasing ("offering") a later
 * phase to the client and committing a phase internally.
 *
 * A phase commitment is carried by a builder-mode change order holding the
 * phase's structure nodeSets — so approval flows through the existing
 * contract-value ledger, CO sales order, QuickBooks invoicing, and procurement
 * machinery without special cases.
 */

import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from './firebase';
import type { ChangeOrder, ProposalBuilderData } from '../types';
import { createChangeOrder, getChangeOrderById, updateChangeOrder } from './changeOrders';
import {
  approveChangeOrderInternally,
  type ApproveChangeOrderInternallyOptions,
} from './changeOrderSync';
import { getPhaseNodeSets, getProposalPhaseById } from './proposalPhases';
import { effectiveBondingPercent } from './proposalBonding';
import { removeUndefinedDeep } from './removeUndefinedDeep';

export interface PhaseChangeOrderContext {
  /** proposalBuilders doc id (normally = dealId). */
  proposalId: string;
  proposal: ProposalBuilderData;
  phaseId: string;
  userId: string;
  /** timberProjects id, when the deal has been promoted (procurement group linkage). */
  projectId?: string;
}

async function writePhasePatch(
  ctx: PhaseChangeOrderContext,
  patch: (phase: NonNullable<ProposalBuilderData['phases']>[number]) => Record<string, unknown>,
): Promise<void> {
  const phases = (ctx.proposal.phases || []).map(p =>
    p.id === ctx.phaseId ? { ...p, ...patch(p) } : p,
  );
  await updateDoc(doc(db, 'proposalBuilders', ctx.proposalId), {
    phases: removeUndefinedDeep(phases),
    updatedAt: Timestamp.now(),
  });
  ctx.proposal.phases = phases as ProposalBuilderData['phases'];
}

/**
 * Ensure a builder-mode CO exists for the phase, snapshotting the phase's
 * CURRENT structure pricing (this is how staff repricing reaches the client:
 * edit pricing in the builder, then offer/re-offer the phase).
 */
export async function ensurePhaseChangeOrder(ctx: PhaseChangeOrderContext): Promise<ChangeOrder> {
  const phase = getProposalPhaseById(ctx.proposal, ctx.phaseId);
  if (!phase) throw new Error('Phase not found on this proposal');
  if (phase.status === 'committed') throw new Error('Phase is already committed');

  const nodeSets = getPhaseNodeSets(ctx.proposal, ctx.phaseId).map(ns => ({
    ...ns,
    enabled: true,
    clientOptional: false,
  }));
  if (nodeSets.length === 0) throw new Error('This phase has no structures assigned');
  // Phase commitments ride the same bonding pass-through as the signed proposal.
  const bondingPercent = effectiveBondingPercent(ctx.proposal.globalCosts) || undefined;

  if (phase.changeOrderId) {
    const existing = await getChangeOrderById(phase.changeOrderId);
    if (existing && existing.id && existing.status !== 'approved' && existing.status !== 'void') {
      // Refresh pricing snapshot from the builder's current structure pricing.
      await updateChangeOrder(
        existing.id,
        {
          builderNodeSets: nodeSets,
          builderProfitMargin: ctx.proposal.profitMargin,
          bondingPercent,
          title: `${phase.name} — phase commitment`,
          scopeDescription: `Phase commitment: ${nodeSets.map(n => n.name).join(', ')}`,
        },
        { recomputeFrom: existing },
      );
      return (await getChangeOrderById(existing.id)) || existing;
    }
    if (existing?.status === 'approved') return existing;
  }

  const co = await createChangeOrder({
    dealId: ctx.proposal.dealId,
    projectId: ctx.projectId,
    proposalId: ctx.proposalId,
    title: `${phase.name} — phase commitment`,
    scopeDescription: `Phase commitment: ${nodeSets.map(n => n.name).join(', ')}`,
    pricingMode: 'builder',
    builderNodeSets: nodeSets,
    builderProfitMargin: ctx.proposal.profitMargin,
    bondingPercent,
    clientName: ctx.proposal.clientName,
    clientEmail: ctx.proposal.clientEmail,
    projectName: ctx.proposal.projectName,
    projectNumber: ctx.proposal.projectNumber,
    createdBy: ctx.userId,
  });

  await writePhasePatch(ctx, () => ({ changeOrderId: co.id }));
  return co;
}

/**
 * Release a phase for client commitment: ensures/refreshes the phase CO,
 * marks it sent, and flips the phase to `offered` (repricing stamps
 * `repricedAt` when the phase had a prior snapshot).
 */
export async function offerPhaseToClient(ctx: PhaseChangeOrderContext): Promise<ChangeOrder> {
  const phase = getProposalPhaseById(ctx.proposal, ctx.phaseId);
  if (!phase) throw new Error('Phase not found on this proposal');
  const hadSnapshot = !!phase.changeOrderId;

  const co = await ensurePhaseChangeOrder(ctx);
  if (co.id && co.status === 'draft') {
    await updateChangeOrder(co.id, { status: 'sent', sentAt: new Date() });
  }

  await writePhasePatch(ctx, () => ({
    status: 'offered',
    changeOrderId: co.id,
    offeredAt: new Date(),
    ...(hadSnapshot ? { repricedAt: new Date() } : {}),
  }));
  return co;
}

/**
 * Staff-recorded phase commitment (offline client consent) — mirrors the
 * internal change-order approval path; admin-only, enforced downstream.
 */
export async function commitPhaseInternally(
  ctx: PhaseChangeOrderContext,
  opts: ApproveChangeOrderInternallyOptions,
): Promise<void> {
  const co = await ensurePhaseChangeOrder(ctx);
  if (!co.id) throw new Error('Change order missing id');
  if (co.status === 'approved') return;
  // approveChangeOrderInternally → syncApprovedChangeOrder → markLinkedProposalPhaseCommitted
  await approveChangeOrderInternally(co.id, opts);
}
